import React, { useEffect, useState } from 'react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import WelcomeModal from './WelcomeModal';
import GuidedTour from './GuidedTour';
import HelpCenter from './HelpCenter';
import {
  getOnboardingContent,
  OnboardingContentSettings,
  SupportContentAudience,
} from '../../services/onboardingService';

interface OnboardingWrapperProps {
  children: React.ReactNode;
  userName?: string;
  userRole?: 'kontor' | 'byra' | 'admin';
  supportAudience?: SupportContentAudience;
}

/**
 * Wrapper-komponent som hanterar hela onboarding-flödet.
 * Lägg denna runt huvudinnehållet i App.tsx.
 */
const OnboardingWrapper: React.FC<OnboardingWrapperProps> = ({
  children,
  userName,
  userRole = 'kontor',
  supportAudience = 'all'
}) => {
  const {
    status,
    loading,
    handleWelcomeSeen,
    handleStartTour,
    handleTourStepComplete,
    handleTourComplete,
    handleSkipTour,
    handleResetOnboarding
  } = useOnboarding();

  const [tourRunning, setTourRunning] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [content, setContent] = useState<OnboardingContentSettings | null>(null);

  useEffect(() => {
    if (content || loading || !status) {
      return;
    }

    let isMounted = true;

    const loadContent = async () => {
      try {
        const nextContent = await getOnboardingContent();
        if (isMounted) {
          setContent(nextContent);
        }
      } catch (error) {
        console.error('[Onboarding] Failed to fetch content:', error);
      }
    };

    loadContent();

    return () => {
      isMounted = false;
    };
  }, [content, loading, status]);

  // Visa välkomst-modal när status laddats och användaren inte sett den
  useEffect(() => {
    if (!loading && status && !status.hasSeenWelcome) {
      // Liten fördröjning för att låta UI:t rendera först
      const timer = setTimeout(() => {
        setShowWelcome(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, status]);

  // Hantera när användaren klickar "Visa guide"
  const handleStartTourClick = async () => {
    setShowWelcome(false);
    await handleWelcomeSeen();
    await handleStartTour();
    // Liten fördröjning innan tour startar
    setTimeout(() => {
      setTourRunning(true);
    }, 300);
  };

  // Hantera när användaren hoppar över
  const handleSkipClick = async () => {
    setShowWelcome(false);
    await handleSkipTour();
  };

  // Hantera när tour är klar
  const handleTourEnd = async () => {
    setTourRunning(false);
    await handleTourComplete();
  };

  // Hantera stegbyte i tour
  const handleStepChange = async (stepIndex: number, stepId: string) => {
    await handleTourStepComplete(stepId, stepIndex);
  };

  // Hantera omstart av tour (från HelpCenter)
  const handleRestartTour = async () => {
    await handleResetOnboarding();
    setShowWelcome(true);
  };

  // Visa inget extra under laddning
  if (loading) {
    return <>{children}</>;
  }

  return (
    <>
      {children}

      {/* Välkomst-modal */}
      {content && (
        <WelcomeModal
          open={showWelcome}
          onStartTour={handleStartTourClick}
          onSkip={handleSkipClick}
          userName={userName}
          viewerAudience={supportAudience}
          content={content.welcomeModal}
        />
      )}

      {/* Guidad tour */}
      <GuidedTour
        run={tourRunning}
        onTourEnd={handleTourEnd}
        onStepChange={handleStepChange}
        userRole={userRole}
      />

      {/* Hjälpcenter (alltid synlig) */}
      {status?.preferences?.showHelpButton !== false && content && (
        <HelpCenter
          onRestartTour={handleRestartTour}
          content={content.helpCenter}
          viewerAudience={supportAudience}
        />
      )}
    </>
  );
};

export default OnboardingWrapper;

